import {
  type MouseEvent as ReactMouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import FeedbackCard from 'components/FeedbackCard/FeedbackCard'
import backArrowIcon from 'assets/img/back-arrow.svg'
import forwardArrowIcon from 'assets/img/forward-arrow.svg'
import ReloadIconSvg from 'assets/csvs/ReloadIconSvg'
import { useTheme } from 'styled-components'
import { ReloadIconButton } from 'common/common-styles'
import {
  ActivityFeedHeader,
  CarouselButton,
  CarouselButtonIcon,
  CarouselContainer,
  CarouselShell,
  CarouselSlide,
  CarouselTrack,
  CarouselViewport,
  SectionHeading
} from './styles'

interface ActivityFeedCard {
  id: number
  title: string
  label: string
  icon: string
  type: string
  formativeId?: number
  summativeId?: number
}

interface Props {
  activityCards: ActivityFeedCard[]
  isAssessmentsPending: boolean
  isReloadDisabled: boolean
  isDismissingNotification: boolean
  dismissingNotificationId?: unknown
  onReload: () => Promise<unknown>
  onDismissCard: (cardId: number) => void
  onCardClick: (card: ActivityFeedCard) => void
}

const ActivityFeedCarousel = ({
  activityCards,
  isAssessmentsPending,
  isReloadDisabled,
  isDismissingNotification,
  dismissingNotificationId,
  onReload,
  onDismissCard,
  onCardClick
}: Props) => {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [cardsPerView, setCardsPerView] = useState(2)
  const [isDragging, setIsDragging] = useState(false)
  const [dragOffset, setDragOffset] = useState(0)
  const [isReloading, setIsReloading] = useState(false)
  const theme = useTheme()

  const dragStartXRef = useRef<number | null>(null)
  const dragDeltaRef = useRef(0)
  const hasDraggedRef = useRef(false)

  const cardStepPx = 344
  const dragThresholdPx = 60
  const dragFlagThresholdPx = 6
  const maxVisualDragPx = 120

  const minCarouselCards = 3
  const loadingCards = Array.from({ length: minCarouselCards }, (_, index) => ({
    id: index
  }))

  const shouldRenderOnlyLoadingCards =
    isAssessmentsPending || activityCards.length === 0

  const placeholderCards = useMemo(
    () =>
      Array.from(
        { length: Math.max(0, minCarouselCards - activityCards.length) },
        (_, index) => ({
          id: `placeholder-${index}`
        })
      ),
    [activityCards.length]
  )

  const renderedCardsCount = shouldRenderOnlyLoadingCards
    ? loadingCards.length
    : activityCards.length + placeholderCards.length

  const maxIndex = Math.max(0, renderedCardsCount - cardsPerView)
  const isDragEnabled =
    !shouldRenderOnlyLoadingCards && maxIndex > 0 && placeholderCards.length === 0

  useEffect(() => {
    const onResize = () => {
      setCardsPerView(window.innerWidth < 900 ? 1 : 2)
    }

    onResize()
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  useEffect(() => {
    setCurrentIndex((prev) => Math.min(prev, maxIndex))
  }, [maxIndex])

  useEffect(() => {
    setCurrentIndex(0)
  }, [activityCards, shouldRenderOnlyLoadingCards])

  const onNext = () => {
    setCurrentIndex((prev) => Math.min(prev + 1, maxIndex))
  }

  const onPrevious = () => {
    setCurrentIndex((prev) => Math.max(prev - 1, 0))
  }

  /**
   * Keep the teacher-portal rotation active for the full classroom request and
   * prevent overlapping reloads while the selected classroom is refreshing.
   */
  const handleReload = async () => {
    if (isReloading || isAssessmentsPending || isReloadDisabled) return

    setIsReloading(true)
    try {
      await onReload()
    } finally {
      setIsReloading(false)
    }
  }

  const endDrag = useCallback(() => {
    const delta = dragDeltaRef.current
    const shouldSuppressClick = Math.abs(delta) > dragFlagThresholdPx

    if (Math.abs(delta) >= dragThresholdPx) {
      setCurrentIndex((prev) => {
        if (delta < 0) {
          return Math.min(prev + 1, maxIndex)
        }

        return Math.max(prev - 1, 0)
      })
    }

    dragStartXRef.current = null
    dragDeltaRef.current = 0
    setDragOffset(0)
    setIsDragging(false)

    if (shouldSuppressClick) {
      window.setTimeout(() => {
        hasDraggedRef.current = false
      }, 0)
    }
  }, [dragFlagThresholdPx, dragThresholdPx, maxIndex])

  const onDragStart = (event: ReactMouseEvent<HTMLDivElement>) => {
    if (!isDragEnabled) {
      return
    }

    if (event.button !== 0) {
      return
    }

    dragStartXRef.current = event.clientX
    dragDeltaRef.current = 0
    hasDraggedRef.current = false
    setIsDragging(true)
  }

  useEffect(() => {
    if (!isDragging) {
      return
    }

    const onMouseMove = (event: MouseEvent) => {
      if (dragStartXRef.current === null) {
        return
      }

      const rawDelta = event.clientX - dragStartXRef.current
      dragDeltaRef.current = rawDelta

      if (Math.abs(rawDelta) > dragFlagThresholdPx) {
        hasDraggedRef.current = true
      }

      const limitedDelta = Math.max(
        -maxVisualDragPx,
        Math.min(maxVisualDragPx, rawDelta)
      )
      setDragOffset(limitedDelta)
    }

    const onMouseUp = () => {
      endDrag()
    }

    window.addEventListener('mousemove', onMouseMove)
    window.addEventListener('mouseup', onMouseUp)

    return () => {
      window.removeEventListener('mousemove', onMouseMove)
      window.removeEventListener('mouseup', onMouseUp)
    }
  }, [dragFlagThresholdPx, endDrag, isDragging, maxVisualDragPx])

  return (
    <div className='flex'>
      <CarouselContainer>
        <ActivityFeedHeader>
          <SectionHeading style={{ marginLeft: 0 }}>Activity Feed</SectionHeading>
          <ReloadIconButton
            type='button'
            onClick={handleReload}
            disabled={
              isReloading || isAssessmentsPending || isReloadDisabled
            }
            title='Reload'
            aria-label='Reload current classroom'
            aria-busy={isReloading}
          >
            <ReloadIconSvg
              className={isReloading ? 'reloadPage' : ''}
              color={theme.colors.icons.stroke}
            />
          </ReloadIconButton>
        </ActivityFeedHeader>

        <CarouselShell>
          <CarouselButton
            onClick={onPrevious}
            disabled={currentIndex === 0 || shouldRenderOnlyLoadingCards}
          >
            <CarouselButtonIcon src={backArrowIcon} alt='previous cards' />
          </CarouselButton>

          <CarouselViewport
            $showLeftFade={currentIndex > 0}
            $showRightFade={currentIndex < maxIndex}
            $isDragging={isDragging}
            $isDraggable={isDragEnabled}
            onMouseDown={onDragStart}
            onClickCapture={(event) => {
              if (hasDraggedRef.current) {
                event.preventDefault()
                event.stopPropagation()
                hasDraggedRef.current = false
              }
            }}
            onDragStart={(event) => event.preventDefault()}
          >
            <CarouselTrack
              $currentIndex={currentIndex}
              $dragOffset={dragOffset}
              $isDragging={isDragging}
              $stepPx={cardStepPx}
            >
              {shouldRenderOnlyLoadingCards ? (
                loadingCards.map((card) => (
                  <CarouselSlide key={`loading-${card.id}`}>
                    <FeedbackCard loading title='' label='' />
                  </CarouselSlide>
                ))
              ) : (
                <>
                  {activityCards.map((card) => (
                    <CarouselSlide key={card.id}>
                      <FeedbackCard
                        title={card.title}
                        label={card.label}
                        iconSrc={card.icon}
                        onClick={() => onCardClick(card)}
                        isDismissing={
                          isDismissingNotification &&
                          dismissingNotificationId === card.id
                        }
                        onDismiss={() => onDismissCard(card.id)}
                      />
                    </CarouselSlide>
                  ))}
                  {placeholderCards.map((card) => (
                    <CarouselSlide key={card.id}>
                      <FeedbackCard loading title='' label='' />
                    </CarouselSlide>
                  ))}
                </>
              )}
            </CarouselTrack>
          </CarouselViewport>

          <CarouselButton
            onClick={onNext}
            disabled={currentIndex === maxIndex || shouldRenderOnlyLoadingCards || placeholderCards?.length > 0}
          >
            <CarouselButtonIcon src={forwardArrowIcon} alt='next cards' />
          </CarouselButton>
        </CarouselShell>
      </CarouselContainer>
    </div>
  )
}

export default ActivityFeedCarousel
